import type { AxisKind, ExternalCodeMatch, MappingSystem, MatchMode, ValueType } from './classification.enums.js'

// ─── Registry: axes (the 11 dimensions + 6 attributes are rows) ──────────────

export interface IAxis {
	axisId: string
	tenantId: number
	code: string // stable; e.g. 'species' | 'geography' | 'license_link'
	name: string // display label
	kind: AxisKind
	isHierarchical: boolean
	valueType: ValueType
	status: string // 'active' | …
}

// ─── Values (one table for every axis; hierarchy via parent + closure) ───────

export interface IAxisValue {
	valueId: string
	axisId: string
	parentId: string | null
	code: string // stable
	label: string // display-only, freely editable
	status: string
	validFrom: Date
	validTo: Date | null // retire, never delete
	meta: Record<string, unknown>
}

/** A value with its children — the shape the value-manager tree renders. */
export interface IAxisValueNode extends IAxisValue {
	children: IAxisValueNode[]
}

// ─── One value, many names: taxonomy ↔ Veeva ↔ Workfront (geo-scoped) ────────

export interface IValueMapping {
	valueId: string
	system: MappingSystem
	geoValueId: string | null // NULL = worldwide default; else per-market override
	externalCode: string
}

/** A value an external code resolved to, by id, code and label. */
export interface IExternalCodeValue {
	valueId: string
	code: string
	label: string
}

/** A value mapping with the axis and value it names, for an integration that lists its own. */
export interface IExternalValueMapping {
	axisCode: string
	valueId: string
	valueCode: string
	valueLabel: string
	geoValueId: string | null
	externalCode: string
}

/** One external code's resolution. */
export interface IExternalCodeResolution {
	/** The code as it was asked for. */
	code: string
	match: ExternalCodeMatch
	value: IExternalCodeValue | null
	/** Every value claiming the code when it is ambiguous; empty otherwise. */
	candidates: IExternalCodeValue[]
}

// ─── Cross-axis validity: in which markets a value exists (Veeva per-country) ─

export interface IAxisValueAvailability {
	valueId: string
	geoValueId: string
}

/**
 * A value's names per market and where it is available, for a caller that matches text per
 * market (ADR-0041). A market is a geography value code; `null` is the worldwide row.
 */
export interface IValueMarketNames {
	valueId: string
	/** Its `taxonomy_title` names. */
	names: { geoCode: string | null; name: string }[]
	/** Where it is sold. Empty means nobody has described it, not "everywhere". */
	availableIn: (string | null)[]
	/** Its Vault record id (its `veeva` mapping): a product's, a product family's or a brand's; null when it has none. */
	veevaId: string | null
}

// ─── Polymorphic anchor + tags ───────────────────────────────────────────────

export interface IClassifiedObject {
	objectType: string // 'file' | 'rule' | 'prompt' | …
	objectId: string
	tenantId: number
	createdAt: Date
}

/**
 * The tuple every write lands in when the caller names none. Tuples are ordinals
 * within an object (1, 2, …), so an object classified without any tuple awareness
 * has exactly one tuple — indistinguishable from how objects were stored before
 * tuples existed. This is what keeps the change invisible to consumers that never
 * opt in.
 */
export const DEFAULT_TUPLE_ID = 1

/**
 * A single dimension tag. No rows for an axis = applies to ALL (ADR-0007), scoped
 * within the tuple. `tupleId` groups tags into independent assertions about the
 * object: the same axis may hold different values in different tuples.
 */
export interface IClassification {
	objectType: string
	objectId: string
	tupleId: number
	axisId: string
	valueId: string
}

/** Free-value attribute: License Link, Aspect Ratio, dates… */
export interface IObjectAttribute {
	objectType: string
	objectId: string
	axisId: string
	value: unknown // jsonb
}

// ─── Resolution query shapes (engine + API) ──────────────────────────────────

/** A per-axis selection of value codes, keyed by axis code. */
export type AxisSelection = Record<string, string[]>

export interface ClassifyInput {
	objectType: string
	objectId: string
	axisCode: string
	valueCode: string
}

export interface MatchingInput {
	objectType: string
	context: AxisSelection
	mode?: MatchMode // default STRICT
}

export interface SearchInput {
	objectType: string
	filters: AxisSelection
}

/** A matched object reference returned by coverage/browse. */
export interface ObjectRef {
	objectType: string
	objectId: string
}

/**
 * One resolved classification tag on an object — codes plus the value's display
 * label, and the tuple it belongs to.
 *
 * `tupleId` is optional so a caller that has not opted into tuples keeps seeing
 * the union of an object's tags exactly as before; group by it to render or
 * reason about the object's separate assertions.
 */
export interface ObjectTag {
	axisCode: string
	valueCode: string
	label: string
	tupleId?: number
}

/** One of an object's tuples: its ordinal and the tags asserted in it. */
export interface ObjectTagTuple {
	tupleId: number
	tags: ObjectTag[]
}

/**
 * An object's resolved tags — the unit returned by the batch object-tags read.
 *
 * `tags` is the UNION across every tuple, unchanged from before tuples existed,
 * so a caller that has not opted in keeps working exactly as it did. `tuples`
 * is the same tags grouped into the object's separate assertions — read it when
 * the distinction matters (e.g. rendering, or asking "is this object in campaign
 * c123?"), because the union cannot tell you which values belong together.
 */
export interface ObjectTags {
	objectId: string
	tags: ObjectTag[]
	tuples: ObjectTagTuple[]
}

/** An object's `object_attribute` values keyed by axis code — the unit returned by the batch object-attributes read. */
export interface ObjectAttributes {
	objectId: string
	attributes: Record<string, string[]>
}

// ─── Resolution explainability ───────────────────────────────────────────────

/**
 * Why one axis decided the way it did, inside one tuple.
 *
 *  - `covered` — the tuple constrains this axis and one of its values covers a
 *    context value through the closure. `objectValue`/`contextValue` name the
 *    specific pair that carried it.
 *  - `unconstrained` — the tuple has no tag on this axis, so it imposes nothing
 *    (`empty = ALL`).
 *  - `undeclared-by-context` — the tuple constrains the axis but the context
 *    never mentions it, so it cannot be covered. Only reachable in strict mode.
 *  - `not-covered` — the context declared the axis, but no tagged value is an
 *    ancestor-or-self of any value it asked for.
 *
 * The last two are deliberately distinct: "you didn't ask about this" and "you
 * asked and the answer was no" are different diagnoses, and collapsing them is
 * what makes a match hard to explain.
 */
export type AxisDecisionOutcome = 'covered' | 'unconstrained' | 'undeclared-by-context' | 'not-covered'

export interface AxisDecision {
	axisCode: string
	outcome: AxisDecisionOutcome
	/** The tuple's tagged values on this axis (empty when unconstrained). */
	objectValues: string[]
	/** The values the context asked for on this axis (empty when undeclared). */
	contextValues: string[]
	/** For `covered`: the tagged value that covered `contextValue` via the closure. */
	objectValue?: string
	/** For `covered`: the context value that was covered. */
	contextValue?: string
}

/** How one of an object's tuples fared against the context. */
export interface TupleExplanation {
	tupleId: number
	matched: boolean
	/** Per-axis decisions, in evaluation order. */
	axes: AxisDecision[]
	/** For a non-match, the axis that decided it — the first one to fail. */
	failedOnAxis?: string
}

/**
 * The structured result of explaining a coverage decision. Data only: no
 * human-readable message, ever. Explain output describes taxonomy internals
 * (axis codes, value codes, tuple ordinals, match mode), so formatting it for an
 * end user leaks the model — that belongs to whatever surface presents it.
 */
export interface CoverageExplanation {
	/** The boolean result. `matches = explain(...).verdict` — they cannot disagree. */
	verdict: boolean
	mode: MatchMode
	/** Ordinals of every tuple that matched. Empty when the verdict is false. */
	matchingTupleIds: number[]
	/** Every tuple's evaluation, ascending by ordinal. */
	tuples: TupleExplanation[]
}

/** Resolved external code for a value in a geography (specificity-resolved). */
export interface ResolvedMapping {
	valueId: string
	system: MappingSystem
	externalCode: string
	/** How the code was resolved: exact-country > region > worldwide default. */
	resolvedVia: 'country' | 'region' | 'default'
}

// ─── Facets (active-values drilldown primitive) ──────────────────────────────

/** One active value of a faceted axis with its object count. */
export interface FacetValue {
	valueId: string
	code: string
	label: string
	count: number
}

/** Active values for many axes under one accumulated filter — the batch facet response, keyed by axis code. */
export type FacetBatchResult = Record<string, FacetValue[]>

// ─── Bootstrap (session-cacheable registry read) ─────────────────────────────

/** A value's entry in the bootstrap payload — the value plus its server-computed hierarchy depth. */
export interface IBootstrapAxisValue {
	valueId: string
	axisId: string
	parentId: string | null
	code: string
	label: string
	meta: Record<string, unknown>
	/** Distance from the axis's root value (0 for a root or a non-hierarchical axis). */
	depth: number
}

/**
 * The whole active axis registry in one call: every active axis (any kind/
 * valueType), plus — for `value_type='list'` axes only — their active values
 * with depth. Non-list axes are simply absent from `valuesByAxis`. Read-model
 * data (no dates), like the facet engine and the Explore funnel — the
 * controller passes it straight out.
 */
export interface IClassificationBootstrap {
	axes: IAxis[]
	valuesByAxis: Record<string, IBootstrapAxisValue[]>
}

// ─── Explore funnel (guided region → country → brand drilldown) ──────────────

/**
 * A brand present in a market, as reached through the funnel. `assetCount` is the
 * number of distinct assets that brand has IN that market — the ordering signal
 * behind "top brands".
 */
export interface IExploreBrandRef {
	brandId: string
	code: string
	/**
	 * The brand's name IN THIS MARKET — its `taxonomy_title` mapping where one exists, else its own
	 * name. Banamine reads "Finadyne" under EMEA, because that is what it is called there and a
	 * market list that named it otherwise would name a product nobody in that market recognises.
	 */
	name: string
	/**
	 * Every name this brand goes by anywhere, including its own — what a SEARCH must match.
	 *
	 * Someone looking for Finadyne should find it whatever market they are browsing, and someone
	 * who knows it as Banamine should find the same brand. Matching only the resolved name would
	 * make a brand unfindable under the name half the world uses for it.
	 */
	aliases: string[]
	assetCount: number
	/**
	 * The brand's DISTINCT assets across every market in the read — the number a brand card shows.
	 *
	 * Separate from `assetCount` because the two cannot be derived from one another. Counting is
	 * coverage-based, so a file tagged `emea` genuinely counts `1` in Spain, Italy and every other
	 * EMEA market; summing those per-market answers yields assets×markets, which is how 8 Allflex
	 * files came to read "34 assets". Only a count taken over the union of the markets can say how
	 * many FILES there are, and the union is visible on the server, never to a client holding one
	 * market's slice.
	 *
	 * Same value on every market row of one read — it describes the brand across the scope, not the
	 * market the row is about.
	 */
	totalAssetCount: number
}

/** A country of a region with the brands that have assets in it. */
export interface IExploreCountry {
	code: string
	label: string
	brandCount: number
	/** Ordered by `assetCount` desc, then name — the full list, never truncated. */
	brands: IExploreBrandRef[]
}

/**
 * A region with rolled-up counts over its countries: how many of them hold
 * asset-backed brands, and how many distinct brands across them. A region with
 * `countryCount: 0` has nothing to browse.
 */
export interface IExploreRegion {
	code: string
	label: string
	countryCount: number
	brandCount: number
}

// ─── Promotable objects: Brand / Product ─────────────────────────────────────

/**
 * Fields shared by every promoted object (a dimension/attribute value graduated
 * to a first-class object). `valueId` links 1:1 to its canonical `axis_value`.
 */
export interface IPromotableEntity {
	tenantId: number
	code: string // stable; mirrors the axis_value code
	name: string
	status: string // 'active' | 'retired'
	valueId: string // → its axis_value
	meta: Record<string, unknown>
	createdAt: Date
	updatedAt: Date
}

export interface IBrand extends IPromotableEntity {
	brandId: string
	description: string | null
	logoRef: string | null // app-layer ref, no cross-service FK
	businessUnit: string | null // convenience mirror of the business_unit attribute
}

export interface IProduct extends IPromotableEntity {
	productId: string
	brandId: string
	subtitle: string | null
	therapeuticClass: string | null
	principalClaim: string | null
	durationSeal: string | null
	imageRef: string | null
}

/**
 * The market a page is scoped to: the resolved geography value. `null` on a page
 * when no market is asked for, or when the requested geography code resolves to
 * no active value — a stale link degrades to the unscoped view rather than erroring.
 */
export interface IPageMarket {
	code: string
	label: string
}

/**
 * A value a page can be resolved for on one axis: one its assets are actually
 * classified to, with how many of them roll up to it through the closure. Every level
 * of the axis appears — a region as well as its countries, a species as well as its
 * sub-species — because a scope handed over from elsewhere can be at either depth.
 * `parentCode` is the value directly above it (`null` at the top of the axis), so a
 * picker can group without a second read.
 *
 * These come from the ASSET facet, not from the object's own tags: geography is carried
 * by a product's assets rather than by the product, and its assets are also the only
 * honest source for which species there is actually content for.
 */
export interface IPageAxisValue extends IPageMarket {
	assetCount: number
	parentCode: string | null
}

/**
 * One geography scope an object is called something other than its own name in — a market, a
 * region, or the axis root — with the scope resolved to what a consumer actually holds: a
 * stable code and a label, never a database id.
 *
 * Named for the `value_mapping` row it projects rather than for a product: the same shape
 * carries a brand's names across markets, promoted objects being classified alike.
 *
 * The projection exists because `IValueMapping` cannot be rendered: its `geoValueId` is a
 * `bigserial` id, and no page read carries an id→code bridge for the geography axis. A
 * consumer given the raw rows can only print `geo #71`.
 *
 * Always scoped. An object's unscoped name is the `name` column on its own table, so an
 * unscoped naming row would be a second, competing home for the same fact — these rows say
 * where the object is called something ELSE. A row scoped to the axis root is a different
 * statement and does appear: it says the object is called something else everywhere.
 *
 * Title rows only: this is an object's naming surface, and an integration identifier is
 * not a name. The raw rows for every system stay on the read beside it.
 */
export interface IMappedName {
	/** The geography value this name applies at — a market, a region, or the axis root. */
	geoCode: string
	/** The geography value's label, as a picker would show it. */
	geoLabel: string
	name: string
}

/**
 * A product-page composition — product + brand + the tags and names needed to
 * render them.
 *
 * Carries no assets: an `ObjectRef` is an id and a type, which cannot render an
 * asset, so a consumer showing the product's assets reads them from file
 * discovery (each file with its tags) — one read answering both which and how many.
 */
export interface IProductPage {
	product: IProduct
	brand: IBrand | null
	/** The product's own classification tags. Mirrors `IBrandPage.brandTags` so both page reads carry one tag shape. */
	productTags: ObjectTag[]
	/**
	 * Per-market external names — the value_mapping rows on the product's value, for EVERY
	 * system. The raw truth, kept so `names` and `resolvedName` cannot disagree with it about
	 * which rows exist; not renderable on its own (see {@link IMappedName}).
	 */
	namesByMarket: IValueMapping[]
	/**
	 * The product's naming surface, ready to render: the scopes it is called something other
	 * than `product.name` in, each resolved to a code and a label, ordered general to specific.
	 * Projected from `namesByMarket`, and holding exactly the rows `resolvedName` may be
	 * resolved from.
	 */
	names: IMappedName[]
	/**
	 * The product's name for `market`, resolved from `namesByMarket` by specificity among
	 * `taxonomy_title` rows only — the market, then each geography above it up to the axis
	 * root — falling back to the product's own name, which is also what an unscoped page shows.
	 * An integration identifier is never a name, so a `veeva` or `workfront` row cannot win
	 * here however specific its scope.
	 * Resolved here because `IValueMapping.geoValueId` is a database id a client never holds.
	 */
	resolvedName: string
	market: IPageMarket | null
	/**
	 * What the page can be resolved for, keyed by AXIS CODE: per axis, the values the
	 * product's assets carry, counted the same closure-aware way as the Explore funnel —
	 * so a count here agrees with the card the scope was reached through. Each axis is
	 * counted under the REST of the tuple, making the set a facet drilldown rather than
	 * a fixed list.
	 *
	 * A map rather than a field per axis because axes are data: resolving the page on one
	 * more of them is a change of which axes are asked for, not of this shape.
	 */
	resolvableValues: Record<string, IPageAxisValue[]>
	/**
	 * What APPLIES in the resolved tuple, keyed by axis code: the value resolved for,
	 * then each ancestor above it, most specific first (`['noram_us', 'noram', 'global']`).
	 * An axis with nothing resolved is absent, so an empty map is an unscoped page.
	 *
	 * The ladder `resolvedName` is resolved through, exposed because a consumer narrowing
	 * scope-agnostic content (a brand kit, a template) needs "applies here" — which a
	 * classification filter cannot express, matching a value's DESCENDANTS rather than
	 * its ancestors. Its first entry is the value actually resolved for, so a consumer
	 * never has to trust a raw request parameter.
	 */
	appliesTo: Record<string, string[]>
	/** Matching rules for this product's context, narrowed by every part of the tuple that resolved. */
	rules: ObjectRef[]
}

/** An axis value reduced to what a picker needs — code to filter by, label to show. */
export interface IAxisValueOption {
	code: string
	label: string
}

/**
 * The market a brand page is scoped to — the resolved geography value plus how many
 * distinct assets the brand has in it. The count is brand-specific (it comes from a
 * brand-keyed pairing read), which is why it sits here rather than on `IPageMarket`.
 */
export interface IBrandPageMarket extends IPageMarket {
	assetCount: number
	/**
	 * The market and every scope above it, most specific first — `emea_es → emea → global`.
	 *
	 * The APPLICABILITY LADDER, and what lets a consumer narrow content to a market without
	 * understating it. A classification filter matches a value's DESCENDANTS, so filtering on the
	 * market code alone reaches only that code and drops the region-wide artwork that genuinely
	 * applies there; filtering on the whole ladder keeps it and still excludes sibling markets.
	 *
	 * Resolved server-side because it walks `parent_id`, which a client does not hold. Mirrors
	 * `IProductPage.appliesTo` for the geography axis.
	 */
	appliesTo: string[]
}

/**
 * A brand-page composition — the brand, its products, and the tags/options needed
 * to render and filter them. Sibling of `IProductPage`.
 *
 * `productTags` is keyed by `productId` rather than being an array of `ObjectTags`
 * so the page can look up one product's tags without scanning; `audiences` carries
 * the audience axis's values so the product filter is taxonomy-driven rather than
 * a list hardcoded in the client.
 */
export interface IBrandPage {
	brand: IBrand
	/** The brand object's own classification tags — backs the species/type chips. */
	brandTags: ObjectTag[]
	products: IProduct[]
	productTags: Record<string, ObjectTag[]>
	audiences: IAxisValueOption[]
	market: IBrandPageMarket | null
	/**
	 * Every scope this brand is called something OTHER than `brand.name` in.
	 *
	 * A brand's name is region-split more often than a product's — the same molecule ships as
	 * Banamine in the US and Finadyne in the EU — and that is data on the axis value, not a
	 * string on the record, so it has to be resolved and projected server-side: the rows carry
	 * `geo_value_id` database ids, and a client holds only stable codes.
	 *
	 * Mirrors `IProductPage.names`, so both page reads carry one naming shape.
	 */
	names: IMappedName[]
	/**
	 * The brand's name for the market this page is scoped to — its own mapping, else the
	 * nearest scope above it, else `brand.name`.
	 *
	 * Always populated: with no market resolved it is `brand.name`, so a consumer can render
	 * this without first asking whether a market was asked for.
	 */
	resolvedName: string
	/**
	 * Every value_mapping row on the brand's value, for EVERY system — the raw truth behind
	 * `names`, which narrows to the rows that can become a NAME and so drops the Veeva
	 * identity. Mirrors `IProductPage.namesByMarket`.
	 */
	namesByMarket: IValueMapping[]
	/**
	 * The AXIS VALUE's `meta`, as opposed to the brand row's: the taxonomy load writes the
	 * brand's Veeva provenance here, and the page reads it as stored.
	 */
	brandValueMeta: Record<string, unknown>
}
